import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { cn, relativeTime } from '@/lib/utils'
import type { Profile } from '@/types/api'

// ---------------------------------------------------------------------------
// Viewport
// ---------------------------------------------------------------------------
const PHONE_QUERY = '(max-width: 767px)'

/** True while the media query matches; the first render already knows. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const list = window.matchMedia(query)
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/** Below the `md` breakpoint: bottom tab bar, sheets instead of inline editors. */
export const useIsPhone = () => useMediaQuery(PHONE_QUERY)

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
export type ProfileTabKey =
  | 'profile'
  | 'skills'
  | 'work-history'
  | 'preferences'
  | 'portals'
  | 'preview'
  | 'subscription'

export const PROFILE_TABS: Array<{ key: ProfileTabKey; label: string; path: string }> = [
  { key: 'profile', label: 'Profile', path: '/dashboard/profile' },
  { key: 'skills', label: 'Skills', path: '/dashboard/profile/skills' },
  { key: 'work-history', label: 'Work History', path: '/dashboard/profile/work-history' },
  { key: 'preferences', label: 'Preferences', path: '/dashboard/profile/preferences' },
  { key: 'portals', label: 'Job Portals', path: '/dashboard/profile/portals' },
  { key: 'preview', label: 'Preview', path: '/dashboard/profile/preview' },
  { key: 'subscription', label: 'Subscription', path: '/dashboard/profile/subscription' },
]

/** Where a tab lives, optionally with a section or input to scroll to. */
export function tabHref(tab: ProfileTabKey, anchor?: string): string {
  const path = PROFILE_TABS.find((entry) => entry.key === tab)?.path ?? '/dashboard/profile'
  return anchor ? `${path}#${anchor}` : path
}

// ---------------------------------------------------------------------------
// The checklist's free-text notes, split into labelled answers
// ---------------------------------------------------------------------------

/**
 * Facts employers keep asking for that the profile has no column for. Each is
 * asked as its own question and kept as a labelled line in the checklist's
 * additional notes — the text screening answers are drawn from — so nothing
 * has to be composed by hand.
 */
export const NOTE_QUESTIONS = [
  { key: 'familyGovernment', label: 'Family members employed in government', ask: 'Are any family members employed in government?', placeholder: 'Yes or No. Leave blank if you have not confirmed this.', wide: true },
  { key: 'dateOfBirth', label: 'Date of birth', ask: 'Date of birth', type: 'date' },
  { key: 'degree', label: 'Highest degree', ask: 'Highest degree', placeholder: 'B.Tech, Computer Science' },
  { key: 'university', label: 'University', ask: 'University / college', placeholder: 'JNTU Anantapur' },
  { key: 'graduationYear', label: 'Graduation year', ask: 'Graduation year', type: 'number', placeholder: '2011' },
  { key: 'fixedPay', label: 'Current fixed pay per year', ask: 'Current fixed pay (per year)', type: 'number' },
  { key: 'variablePay', label: 'Current variable pay per year', ask: 'Current variable pay (per year)', type: 'number' },
  {
    key: 'cloud',
    label: 'Cloud hands-on experience',
    ask: 'Hands-on cloud experience (Azure / AWS / GCP)',
    placeholder: 'Azure: 4 years, architected and deployed on AKS. AWS: 7 years.',
    wide: true,
  },
  {
    key: 'aiTools',
    label: 'AI coding tools',
    ask: 'AI coding tools you use',
    placeholder: 'Claude and GitHub Copilot, daily',
    wide: true,
  },
  {
    key: 'shift',
    label: 'Shift timings / time zone',
    ask: 'Shift timings / time zones you can work',
    placeholder: 'Day shift IST; open to overlap with US hours',
    wide: true,
  },
  {
    key: 'relocation',
    label: 'Relocation',
    ask: 'Relocation',
    placeholder: 'Open to relocate within India / not open to relocation',
    wide: true,
  },
] as const

export type NoteKey = (typeof NOTE_QUESTIONS)[number]['key']
export type NoteValues = Record<NoteKey, string>

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')

/** Split the notes into the answered questions and whatever else was written. */
export function parseNotes(text: string | null | undefined): { values: NoteValues; rest: string } {
  const values = Object.fromEntries(NOTE_QUESTIONS.map((q) => [q.key, ''])) as NoteValues
  const rest: string[] = []
  for (const line of (text ?? '').split('\n')) {
    const question = NOTE_QUESTIONS.find((q) =>
      new RegExp(`^\\s*${escapeRegExp(q.label)}\\s*:`, 'i').test(line),
    )
    if (question) values[question.key] = line.slice(line.indexOf(':') + 1).trim()
    else rest.push(line)
  }
  return { values, rest: rest.join('\n').trim() }
}

export function composeNotes(values: NoteValues, rest: string): string | null {
  const lines = NOTE_QUESTIONS.filter((q) => values[q.key].trim()).map(
    (q) => `${q.label}: ${values[q.key].replace(/\s+/g, ' ').trim()}`,
  )
  if (rest.trim()) lines.push(rest.trim())
  return lines.join('\n') || null
}

// ---------------------------------------------------------------------------
// Small formatters
// ---------------------------------------------------------------------------
export const yesNo = (value: boolean) => (value ? 'Yes' : 'No')

export function money(value: number | null | undefined, currency: string | null) {
  if (value == null) return null
  return `${currency ? `${currency} ` : ''}${value.toLocaleString()}`
}

export function initials(profile: Profile): string {
  const parts = [profile.firstName, profile.lastName].filter(Boolean) as string[]
  const source = parts.length ? parts : (profile.fullName ?? '').split(/\s+/).filter(Boolean)
  const letters = source
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
  return letters || '?'
}

export function fileExtension(fileName: string): string {
  const ext = fileName.split('.').pop()
  return ext && ext !== fileName ? ext.slice(0, 4) : 'file'
}

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/** "2020-03" -> "Mar 2020"; a bare year stays a year. */
export function formatMonth(value: string | null | undefined): string | null {
  if (!value) return null
  const [year, month] = value.split('-')
  const index = Number(month) - 1
  const name = MONTHS[index]
  return name && year ? `${name} ${year}` : year ?? null
}

export function experienceDates(experience: {
  startDate: string | null
  endDate: string | null
  isCurrent: boolean
}): string | null {
  const start = formatMonth(experience.startDate)
  const end = experience.isCurrent ? 'Present' : formatMonth(experience.endDate)
  if (!start && !end) return null
  return [start ?? '…', end ?? '…'].join(' – ')
}

// ---------------------------------------------------------------------------
// Section card: title, grey subtitle, body, and its own Save
// ---------------------------------------------------------------------------
/** One column on phones, two from md: the grid every profile form uses. */
export const FORM_GRID = 'grid gap-4 md:grid-cols-2'
export const FORM_SPAN = 'md:col-span-2'

export function SectionCard({
  id,
  title,
  description,
  eyebrow,
  actions,
  children,
  footer,
  className,
}: {
  id?: string
  title: ReactNode
  description?: ReactNode
  /** A small word above the title, e.g. "Work eligibility". */
  eyebrow?: ReactNode
  /** Right of the title: a count, a pill, a small button. */
  actions?: ReactNode
  children: ReactNode
  /** A `SaveFooter`, usually. */
  footer?: ReactNode
  className?: string
}) {
  return (
    <section
      id={id}
      className={cn('card scroll-mt-[120px] p-4 sm:p-6 md:scroll-mt-24', className)}
    >
      <header className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 sm:mb-5">
        <div className="min-w-0">
          {eyebrow && <p className="label-eyebrow mb-1">{eyebrow}</p>}
          <h2 className="text-[16px] font-semibold tracking-tight text-ink">{title}</h2>
          {description && (
            <p className="mt-1 max-w-[640px] text-[13px] leading-relaxed text-ink-muted">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {children}
      {footer}
    </section>
  )
}

/** A titled group of fields inside a section: a flat panel, one column on phones. */
export function FieldGroup({
  id,
  title,
  description,
  children,
  className,
  grid = true,
}: {
  id?: string
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  /** False when the children lay themselves out. */
  grid?: boolean
}) {
  return (
    <section id={id} className={cn('panel scroll-mt-[120px] p-4 md:scroll-mt-24 sm:p-5', className)}>
      <h3 className="label-eyebrow">{title}</h3>
      {description && <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{description}</p>}
      <div className={cn('mt-3.5', grid && FORM_GRID)}>{children}</div>
    </section>
  )
}

/**
 * The Save row of a section. On a phone it is a sticky bar pinned above the
 * tab bar while the section is on screen, with "Unsaved changes" or
 * "Saved · 2 minutes ago" beside the button; on a desktop it sits at the foot
 * of the form.
 */
export function SaveFooter({
  onSave,
  saving,
  dirty = true,
  savedAt,
  hint,
  label = 'Save',
  children,
}: {
  onSave: () => void
  saving: boolean
  dirty?: boolean
  /** When this section was last saved here (ms); shows "Saved · …". */
  savedAt?: number | null
  /** A section-specific note, shown when nothing else is. */
  hint?: ReactNode
  label?: string
  /** Extra controls left of the button. */
  children?: ReactNode
}) {
  const phone = useIsPhone()
  // "2 minutes ago" has to move on its own.
  const [, tick] = useState(0)
  useEffect(() => {
    if (!savedAt) return
    const timer = window.setInterval(() => tick((n) => n + 1), 30_000)
    return () => window.clearInterval(timer)
  }, [savedAt])

  const status = dirty ? (
    <span className="inline-flex items-center gap-1.5 text-warning">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      Unsaved changes
    </span>
  ) : savedAt ? (
    <span className="text-ink-muted">
      Saved · <span className="num">{relativeTime(new Date(savedAt).toISOString())}</span>
    </span>
  ) : (
    hint ?? null
  )

  return (
    <footer
      className={cn(
        'mt-5 flex items-center justify-between gap-3',
        phone
          ? 'action-bar -mb-4 rounded-b-card'
          : 'border-t border-line pt-4',
      )}
    >
      <p className="min-w-0 truncate text-[13px] text-ink-muted" aria-live="polite">
        {status}
        {!dirty && savedAt && hint ? <span className="hidden sm:inline"> · {hint}</span> : null}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        {children}
        <Button size={phone ? 'md' : 'sm'} onClick={onSave} loading={saving} disabled={!dirty}>
          {label}
        </Button>
      </div>
    </footer>
  )
}

/**
 * A section's draft, seeded from the profile. When the profile is refetched
 * (another section saved, or a resume was scanned) the draft follows unless it
 * has unsaved edits, so one section's typing is never wiped by another's save.
 */
export function useSectionForm<T extends object>(profile: Profile, derive: (profile: Profile) => T) {
  const [form, setForm] = useState<T>(() => derive(profile))
  const saved = useRef<string>(JSON.stringify(form))
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [, bump] = useState(0)

  useEffect(() => {
    const next = derive(profile)
    const nextJson = JSON.stringify(next)
    setForm((current) => (JSON.stringify(current) === saved.current ? next : current))
    saved.current = nextJson
    bump((n) => n + 1)
    // `derive` is a module-level function per section; only the profile changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  const update = <K extends keyof T>(key: K, value: T[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const dirty = JSON.stringify(form) !== saved.current

  /** After a successful save: the draft is the truth until the refetch lands. */
  const markSaved = () => {
    saved.current = JSON.stringify(form)
    setSavedAt(Date.now())
    bump((n) => n + 1)
  }

  return { form, setForm, update, dirty, markSaved, savedAt }
}

/** A row of Yes / No pills (the Dice look) for one boolean. */
export function YesNoPills({
  value,
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: {
  value: boolean | null
  onChange: (value: boolean) => void
  disabled?: boolean
  'aria-label'?: string
}) {
  return (
    <div className="flex gap-2" role="radiogroup" aria-label={ariaLabel}>
      {[
        { label: 'Yes', checked: value === true },
        { label: 'No', checked: value === false },
      ].map((option) => (
        <button
          key={option.label}
          type="button"
          role="radio"
          aria-checked={option.checked}
          disabled={disabled}
          onClick={() => onChange(option.label === 'Yes')}
          className={cn(
            'h-10 rounded-pill border px-4 text-[13.5px] font-medium transition-colors',
            option.checked
              ? 'border-ink bg-ink text-oncolor'
              : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
            disabled && 'cursor-not-allowed opacity-55',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('h-4 w-4', className)} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 4h10M6.5 4V2.8a.8.8 0 0 1 .8-.8h1.4a.8.8 0 0 1 .8.8V4M4.2 4l.6 8.4a1 1 0 0 0 1 .9h4.4a1 1 0 0 0 1-.9l.6-8.4M6.6 6.8v4M9.4 6.8v4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('h-3.5 w-3.5', className)} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

/** A text button with a plus: "+ Add skill", "+ Add role". */
export function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-10 items-center gap-1.5 rounded-field px-2.5 text-[14px] font-medium text-brand transition-colors hover:bg-brand-soft"
    >
      <PlusIcon />
      {children}
    </button>
  )
}

/** Red trash icon button used on skill rows and role cards. */
export function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-10 w-10 shrink-0 place-items-center rounded-field text-danger transition-colors hover:bg-danger/10"
    >
      <TrashIcon />
    </button>
  )
}
